import type {
  CashierPaymentMethod,
  CashierPaymentMethodGroup,
  LastUsedPaymentMethod,
  SavedPaymentAccountIdAndFields,
} from "@/api/cashier";
import { useEffect, useMemo, useRef } from "react";

function findMatchingSavedAccount(
  paymentMethod: CashierPaymentMethod,
  paymentAccountDetails: { [key: string]: unknown }
): SavedPaymentAccountIdAndFields | undefined {
  const detailEntries = Object.entries(paymentAccountDetails);
  if (detailEntries.length === 0) return undefined;

  return paymentMethod.saved_payment_accounts?.find((account) => {
    const fields = account.saved_payment_account_fields;
    if (!fields) return false;
    return detailEntries.every(([key, value]) => fields[key] === value);
  });
}

/**
 * Returns the only saved payment account when there is no ambiguity (exactly one exists).
 * When multiple accounts exist, the user must explicitly choose — returns undefined.
 */
export function getUnambiguousSavedAccount(
  paymentMethod: CashierPaymentMethod
): SavedPaymentAccountIdAndFields | undefined {
  const accounts = paymentMethod.saved_payment_accounts;
  return accounts?.length === 1 ? accounts[0] : undefined;
}

export const usePreFillLastUsedPaymentMethod = ({
  paymentMethods,
  lastUsedPaymentMethod,
  selectAccount,
}: {
  paymentMethods?: (CashierPaymentMethod | CashierPaymentMethodGroup)[];
  lastUsedPaymentMethod?: LastUsedPaymentMethod;
  selectAccount: (
    paymentMethod: CashierPaymentMethod,
    paymentMethodGroup?: CashierPaymentMethodGroup,
    savedPaymentAccount?: SavedPaymentAccountIdAndFields
  ) => void;
}) => {
  const appliedTargetId = useRef<string | undefined>(undefined);

  const lastUsedMatch = useMemo(() => {
    if (!paymentMethods || !lastUsedPaymentMethod) return undefined;

    for (const pm of paymentMethods) {
      if ("payment_methods" in pm) {
        const matchedMethod = pm.payment_methods.find(
          (childMethod) =>
            childMethod.id === lastUsedPaymentMethod.payment_method_id
        );
        if (matchedMethod) {
          return { method: matchedMethod, group: pm };
        }
      } else if (pm.id === lastUsedPaymentMethod.payment_method_id) {
        return { method: pm, group: undefined };
      }
    }

    return undefined;
  }, [lastUsedPaymentMethod, paymentMethods]);

  const orderedPaymentMethods = useMemo(() => {
    if (!paymentMethods) return paymentMethods || [];
    const topLevelId = lastUsedMatch?.group?.id ?? lastUsedMatch?.method.id;
    if (!topLevelId) return paymentMethods;
    return [
      ...paymentMethods.filter((pm) => pm.id === topLevelId),
      ...paymentMethods.filter((pm) => pm.id !== topLevelId),
    ];
  }, [lastUsedMatch, paymentMethods]);

  const preSelectedPaymentId = lastUsedMatch
    ? (lastUsedMatch.group?.id ?? lastUsedMatch.method.id)
    : null;
  const hasLasUsedPaymentMethod = Boolean(lastUsedMatch);

  useEffect(() => {
    if (!paymentMethods) {
      return;
    }

    const targetId = lastUsedMatch
      ? lastUsedMatch.method.id
      : paymentMethods[0]?.id;

    if (!targetId || appliedTargetId.current === targetId) {
      return;
    }
    appliedTargetId.current = targetId;

    // If we have a last used payment method select it
    if (lastUsedMatch) {
      const { method: pm, group } = lastUsedMatch;
      const matchedAccount =
        findMatchingSavedAccount(
          pm,
          lastUsedPaymentMethod?.payment_account_details ?? {}
        ) ?? pm.saved_payment_accounts?.[0];
      selectAccount(pm, group, matchedAccount);
      return;
    }

    // No last used method - pre-fill the first payment method
    const firstPaymentMethod = paymentMethods[0];
    if (!firstPaymentMethod) return;

    if ("payment_methods" in firstPaymentMethod) {
      const group = firstPaymentMethod as CashierPaymentMethodGroup;
      const firstGroupPaymentMethod = group.payment_methods?.[0];
      if (firstGroupPaymentMethod) {
        selectAccount(
          firstGroupPaymentMethod,
          group,
          getUnambiguousSavedAccount(firstGroupPaymentMethod)
        );
      }
    } else {
      const paymentMethod = firstPaymentMethod as CashierPaymentMethod;
      selectAccount(
        paymentMethod,
        undefined,
        getUnambiguousSavedAccount(paymentMethod)
      );
    }
  }, [paymentMethods, selectAccount, lastUsedPaymentMethod, lastUsedMatch]);

  return {
    orderedPaymentMethods,
    preSelectedPaymentId,
    hasLasUsedPaymentMethod,
  };
};
